import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { testFetchSource, fetchSourceNow } from "@/lib/news.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Play, FlaskConical } from "lucide-react";

export const Route = createFileRoute("/_authenticated/sources")({ component: Page });

type SrcType = "rss" | "scrape" | "manual" | "api";
const empty = { source_name: "", source_url: "", source_type: "rss" as SrcType, language: "en", fetch_interval_minutes: 60, status: true, asset_category_id: "" };

function Page() {
  const qc = useQueryClient();
  const testFn = useServerFn(testFetchSource);
  const fetchFn = useServerFn(fetchSourceNow);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState(empty);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState<any[]>([]);

  const { data } = useQuery({
    queryKey: ["sources"],
    queryFn: async () => {
      const { data, error } = await supabase.from("news_sources").select("*, asset_categories(name_th, icon)").order("created_at", { ascending: false });
      if (error) throw error; return data ?? [];
    },
  });
  const { data: cats } = useQuery({
    queryKey: ["categories-min"],
    queryFn: async () => (await supabase.from("asset_categories").select("id, name_th, icon").order("sort_order")).data ?? [],
  });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (d: any) => { setEditing(d); setForm({ ...d, asset_category_id: d.asset_category_id ?? "" }); setOpen(true); };

  const save = async () => {
    if (!form.source_name || !form.source_url) return toast.error("กรุณากรอกชื่อและ URL");
    const payload = { ...form, asset_category_id: form.asset_category_id || null };
    const op = editing
      ? supabase.from("news_sources").update(payload as any).eq("id", editing.id)
      : supabase.from("news_sources").insert(payload as any);
    const { error } = await op;
    if (error) return toast.error(error.message);
    toast.success("บันทึกสำเร็จ"); setOpen(false);
    qc.invalidateQueries({ queryKey: ["sources"] });
  };
  const remove = async (id: string) => {
    if (!confirm("ลบแหล่งข่าวนี้?")) return;
    const { error } = await supabase.from("news_sources").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("ลบสำเร็จ");
    qc.invalidateQueries({ queryKey: ["sources"] });
  };
  const test = async (id: string) => {
    try {
      toast.loading("กำลังทดสอบ...", { id: "tf" });
      const r: any = await testFn({ data: { sourceId: id } });
      setPreview(r.preview ?? []); setPreviewOpen(true);
      toast.success(`พบ ${r.count} ข่าว`, { id: "tf" });
      qc.invalidateQueries({ queryKey: ["sources"] });
    } catch (e: any) { toast.error(`ทดสอบล้มเหลว: ${e.message}`, { id: "tf" }); }
  };
  const fetchNow = async (id: string) => {
    try {
      toast.loading("กำลังดึงข่าว...", { id: "fn" });
      const r: any = await fetchFn({ data: { sourceId: id } });
      toast.success(`เพิ่ม ${r.inserted} ข่าว (ซ้ำ ${r.skipped})`, { id: "fn" });
      qc.invalidateQueries({ queryKey: ["sources"] });
    } catch (e: any) { toast.error(`ดึงล้มเหลว: ${e.message}`, { id: "fn" }); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">News Sources</h1>
          <p className="text-sm text-muted-foreground">จัดการแหล่งข่าว RSS/HTML และทดสอบการดึงข้อมูล</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={openNew}><Plus className="h-4 w-4 mr-2"/>เพิ่มแหล่งข่าว</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing ? "แก้ไข" : "เพิ่ม"}แหล่งข่าว</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-2"><Label>ชื่อ</Label><Input value={form.source_name} onChange={(e) => setForm({ ...form, source_name: e.target.value })}/></div>
              <div className="space-y-2"><Label>URL (RSS หรือเว็บ)</Label><Input value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })}/></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>ประเภท</Label>
                  <Select value={form.source_type} onValueChange={(v: any) => setForm({ ...form, source_type: v })}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rss">RSS</SelectItem>
                      <SelectItem value="scrape">HTML Scrape</SelectItem>
                      <SelectItem value="api">API</SelectItem>
                      <SelectItem value="manual">Manual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>ภาษา</Label><Input value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}/></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Asset Category</Label>
                  <Select value={form.asset_category_id || "_none"} onValueChange={(v) => setForm({ ...form, asset_category_id: v === "_none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="เลือก"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— ไม่ระบุ —</SelectItem>
                      {(cats ?? []).map((c: any) => <SelectItem key={c.id} value={c.id}>{c.icon} {c.name_th}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>ความถี่การดึง</Label>
                  <Select value={String(form.fetch_interval_minutes)} onValueChange={(v) => setForm({ ...form, fetch_interval_minutes: Number(v) })}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="60">ทุก 1 ชั่วโมง</SelectItem>
                      <SelectItem value="120">ทุก 2 ชั่วโมง</SelectItem>
                      <SelectItem value="180">ทุก 3 ชั่วโมง</SelectItem>
                      <SelectItem value="360">ทุก 6 ชั่วโมง</SelectItem>
                      <SelectItem value="720">ทุก 12 ชั่วโมง</SelectItem>
                      <SelectItem value="1440">ทุก 24 ชั่วโมง</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center justify-between"><Label>เปิดใช้งาน</Label><Switch checked={form.status} onCheckedChange={(v) => setForm({ ...form, status: v })}/></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>ยกเลิก</Button><Button onClick={save}>บันทึก</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card><CardHeader><CardTitle>รายการแหล่งข่าว</CardTitle><CardDescription>{data?.length ?? 0} รายการ</CardDescription></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>ชื่อ</TableHead><TableHead>Category</TableHead><TableHead>Type</TableHead><TableHead>ความถี่</TableHead><TableHead>สถานะ</TableHead><TableHead>ดึงล่าสุด</TableHead><TableHead>ทดสอบล่าสุด</TableHead><TableHead className="text-right">การกระทำ</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(data ?? []).map((s: any) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">{s.source_name}<div className="text-xs text-muted-foreground truncate max-w-xs">{s.source_url}</div></TableCell>
                  <TableCell className="text-xs">{s.asset_categories?.icon} {s.asset_categories?.name_th ?? "—"}</TableCell>
                  <TableCell><Badge variant="outline">{s.source_type}</Badge></TableCell>
                  <TableCell className="text-xs">{
                    s.fetch_interval_minutes >= 60
                      ? `ทุก ${s.fetch_interval_minutes / 60} ชม.`
                      : `${s.fetch_interval_minutes} นาที`
                  }</TableCell>
                  <TableCell>{s.status ? <Badge>Active</Badge> : <Badge variant="secondary">Off</Badge>}</TableCell>
                  <TableCell className="text-xs">{s.last_fetch_at ? <><div>{new Date(s.last_fetch_at).toLocaleString("th-TH")}</div><div className="text-muted-foreground">{s.last_fetch_status}</div></> : "—"}</TableCell>
                  <TableCell className="text-xs">{s.last_test_status ? <Badge variant={s.last_test_status === "success" ? "default" : "destructive"}>{s.last_test_status}</Badge> : "—"}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => test(s.id)}><FlaskConical className="h-3 w-3 mr-1"/>Test</Button>
                    <Button size="sm" variant="outline" onClick={() => fetchNow(s.id)}><Play className="h-3 w-3 mr-1"/>Fetch</Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(s)}><Pencil className="h-3 w-3"/></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(s.id)}><Trash2 className="h-3 w-3 text-destructive"/></Button>
                  </TableCell>
                </TableRow>
              ))}
              {(data ?? []).length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">ยังไม่มีแหล่งข่าว</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Preview ข่าว 5 รายการแรก</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {preview.map((p, i) => (
              <div key={i} className="border border-border rounded-lg p-3 text-sm">
                <div className="font-medium">{p.title}</div>
                <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{p.description}</div>
                <div className="text-xs text-primary mt-1 truncate">{p.link}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}